import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ComponentType } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { insureds, type InsuredDetail } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DetailError, DetailLayout, DetailLoading } from '@/shared/ui/DetailLayout';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { DocumentsPanel } from '../documents/DocumentsPanel';
import { BlockInsuredDialog } from './BlockInsuredDialog';
import { CadastroCompleto } from './CadastroCompleto';
import { InsuredChangeHistoryTab, InsuredConsultationsTab } from './InsuredHistoryTab';
import { InsuredPartnersTab } from './InsuredPartnersTab';
import { InsuredPoliciesTab } from './InsuredPoliciesTab';
import {
  AVISO_BLOQUEIO,
  PERMISSAO_EDITAR,
  rotuloDoRamo,
  rotuloDoTipo,
} from './rotulos';

interface SecaoDoMenu {
  chave: string;
  titulo: string;
  Componente: ComponentType<{ segurado: InsuredDetail }>;
}

/**
 * Documentos do segurado no painel comum (P-DOC), com "Documentos disponiveis",
 * "Upload de documento" (nomear e enviar de uma vez, no grupo Segurado) e "Documentos
 * excluidos", como o OnPoint. Antes so a operacao tinha o painel (defeito Documentos 6
 * do inventario 47, anexo C).
 */
function Documentos({ segurado }: { segurado: InsuredDetail }) {
  return (
    <DocumentsPanel
      ownerType="segurado"
      ownerId={segurado.id}
      title="Documentos disponiveis"
      description="Documentos do segurado, com o envio, as versoes e os excluidos."
      group="Segurado"
      allowDelete
      emptyTitle="Nenhum documento do segurado"
      emptyDescription="Use Upload de documento para nomear e enviar um arquivo do segurado."
    />
  );
}

/**
 * O menu do detalhe, com as seis secoes do OnPoint e na mesma ordem: Cadastro
 * Completo (P-SEG-1), Socios / Administradores, Apolices, Documentos, Historico de
 * Alteracoes e Historico de Consultas (P-SEG-2).
 */
const secoesDoSegurado: SecaoDoMenu[] = [
  { chave: 'cadastro', titulo: 'Cadastro Completo', Componente: CadastroCompleto },
  {
    chave: 'socios',
    titulo: 'Socios / Administradores',
    Componente: InsuredPartnersTab,
  },
  { chave: 'apolices', titulo: 'Apolices', Componente: InsuredPoliciesTab },
  { chave: 'documentos', titulo: 'Documentos', Componente: Documentos },
  {
    chave: 'alteracoes',
    titulo: 'Historico de Alteracoes',
    Componente: InsuredChangeHistoryTab,
  },
  {
    chave: 'consultas',
    titulo: 'Historico de Consultas',
    Componente: InsuredConsultationsTab,
  },
];

/**
 * Detalhe do segurado ("Visualizar" da lista), no DetailLayout comum (P-FUN-2b).
 *
 * O servidor confere empresa e permissao em cada leitura: a tela esconde o que nao
 * abre, e nao decide o acesso.
 */
export function InsuredDetailPage() {
  const { insuredId = '' } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [bloqueando, setBloqueando] = useState(false);
  const [desbloqueando, setDesbloqueando] = useState(false);

  const consulta = useQuery({
    queryKey: ['insured', insuredId],
    queryFn: ({ signal }) => insureds.get(insuredId, signal),
    enabled: insuredId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['insured', insuredId] });
    void queryClient.invalidateQueries({ queryKey: ['insureds'] });
    void queryClient.invalidateQueries({
      queryKey: ['entity-history', 'insured', insuredId],
    });
  }

  const desbloquear = useMutation({
    mutationFn: () => insureds.unblock(insuredId),
    onSuccess: () => {
      toast.success('Segurado desbloqueado.');
      setDesbloqueando(false);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (consulta.isPending) return <DetailLoading />;

  if (consulta.isError) {
    return (
      <DetailError
        title="Segurado"
        alertTitle="Nao foi possivel abrir o segurado"
        message={consulta.error.message}
        onBack={() => void navigate('/segurados')}
      />
    );
  }

  const segurado = consulta.data;
  const podeEditar = can(PERMISSAO_EDITAR);

  return (
    <>
      <DetailLayout
        title={segurado.legal_name}
        description={`${rotuloDoTipo(segurado.person_kind)} ${formatCpfCnpj(segurado.document)}`}
        navLabel="Secoes do segurado"
        meta={
          <div className="flex flex-wrap gap-1.5">
            {segurado.is_blocked ? (
              <Badge tone="danger">Bloqueado</Badge>
            ) : (
              <Badge tone="success">Ativo</Badge>
            )}
            <Badge>{rotuloDoRamo(segurado.sector)}</Badge>
            {segurado.commercial_registry ? (
              <Badge tone="info">Junta comercial</Badge>
            ) : null}
          </div>
        }
        actions={
          <>
            <Link
              to="/segurados"
              className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              Lista de segurados
            </Link>
            {podeEditar && !segurado.is_blocked ? (
              <Button variant="secondary" onClick={() => setBloqueando(true)}>
                Bloquear
              </Button>
            ) : null}
            {podeEditar && segurado.is_blocked ? (
              <Button variant="secondary" onClick={() => setDesbloqueando(true)}>
                Desbloquear
              </Button>
            ) : null}
          </>
        }
        banner={
          segurado.is_blocked ? (
            <Alert tone="warning" title="Segurado bloqueado">
              <div className="flex flex-col gap-1">
                <p>
                  Bloqueado em {formatDate(segurado.blocked_at)}
                  {segurado.blocked_label ? ` por ${segurado.blocked_label}` : ''}.
                  Motivo: {segurado.block_reason}
                </p>
                <p className="text-2xs">{AVISO_BLOQUEIO}</p>
              </div>
            </Alert>
          ) : undefined
        }
        sections={secoesDoSegurado.map(({ chave, titulo, Componente }) => ({
          key: chave,
          title: titulo,
          render: () => <Componente segurado={segurado} />,
        }))}
      />

      <BlockInsuredDialog
        insured={bloqueando ? segurado : null}
        onClose={() => setBloqueando(false)}
        onDone={() => {
          setBloqueando(false);
          invalidar();
        }}
      />

      <ConfirmDialog
        open={desbloqueando}
        onClose={() => setDesbloqueando(false)}
        onConfirm={() => desbloquear.mutate()}
        title="Desbloquear o segurado?"
        message={`${segurado.legal_name} volta a situacao ativa. O motivo do bloqueio fica no historico.`}
        confirmLabel="Desbloquear"
        loading={desbloquear.isPending}
      />
    </>
  );
}
